/**
 * The patient's care journey, as the appointment overlay offers it (WCP-13254).
 *
 * The roadmap's current step is the suggestion, with each of its forms ready
 * to start or resume. The clinician can work a different step instead from the
 * same place. Nothing is stored: it's read live from the roadmap, so a form
 * submitted during the visit shows as done the next time the roadmap loads.
 */

import {
  CareJourneyRoadmap,
  CareJourneyRoadmapForm,
  CareJourneyRoadmapStep,
  CareJourneyStatus,
} from "../api-helpers/CareJourneyTypes";
import {
  CARE_MANAGEMENT_FORM_ID,
  newCareNotePath,
} from "../pages/care-note/careNoteLinks";

const CARE_NOTE_FORM_TYPE = "care-note";

/** Where to work a journey form, and what to call the button. */
export interface JourneyFormLink {
  label: "Start" | "Resume" | "Start again";
  to: string;
}

export interface JourneyForm {
  clinicalFormId: string;
  name: string;
  /** Care notes are labelled as such in the checklist; assessments aren't. */
  isCareNote: boolean;
  status: CareJourneyStatus;
  /** When the submission counted for this step was made. */
  submittedAt: string | null;
  /** The open draft, while the form is in progress. */
  draftId: string | null;
  /** Start, resume, or (once completed) start the form again. */
  link?: JourneyFormLink;
}

export interface JourneyStep {
  id: string;
  name: string;
  status: CareJourneyStatus;
  isSuggested: boolean;
  isOptional: boolean;
  isCompleted: boolean;
  /** How many of its forms are completed. */
  doneCount: number;
  /** The latest submission among its forms, if any: when it was last worked. */
  lastDoneAt: string | null;
  forms: JourneyForm[];
}

export interface JourneyOverview {
  /** The roadmap's current step; null once the journey is finished. */
  suggested: JourneyStep | null;
  /** Every step with forms, in journey order, to choose from (done ones too). */
  steps: JourneyStep[];
  /** Other focuses, chosen the same way but listed after the journey (a Care Management note). */
  extras?: JourneyStep[];
}

/**
 * Resume the form's draft, or start a new one: a care note opens the Add drawer
 * on that note type, an assessment opens its start page. A completed form can
 * be started again, as a new submission.
 */
export const journeyFormLink = (
  patientId: string,
  form: CareJourneyRoadmapForm
): JourneyFormLink | undefined => {
  if (form.status === "unavailable") {
    return undefined;
  }
  const draftId = form.status === "in-progress" ? form.memberFormId : null;
  const startLabel = form.status === "completed" ? "Start again" : "Start";

  if (form.formType === CARE_NOTE_FORM_TYPE) {
    return draftId
      ? { label: "Resume", to: `/patients/${patientId}/care-note/${draftId}` }
      : { label: startLabel, to: newCareNotePath(patientId, form.clinicalFormId) };
  }
  return draftId
    ? {
        label: "Resume",
        to: `/patients/${patientId}/assessments/${form.clinicalFormId}/resume/${draftId}`,
      }
    : {
        label: startLabel,
        to: `/patients/${patientId}/assessments/${form.clinicalFormId}/start`,
      };
};

const toJourneyStep = (
  step: CareJourneyRoadmapStep,
  patientId: string,
  suggestedId: string | null
): JourneyStep => {
  const submitted = step.forms
    .map((form) => form.submittedAt)
    .filter((at): at is string => !!at)
    .sort();
  return {
    id: step.id,
    name: step.name,
    status: step.status,
    isSuggested: step.id === suggestedId,
    isOptional: step.isOptional,
    isCompleted: step.status === "completed",
    doneCount: step.forms.filter((form) => form.status === "completed").length,
    lastDoneAt: submitted[submitted.length - 1] ?? null,
    forms: step.forms.map((form) => ({
      clinicalFormId: form.clinicalFormId,
      name: form.name,
      isCareNote: form.formType === CARE_NOTE_FORM_TYPE,
      status: form.status,
      submittedAt: form.submittedAt,
      draftId: form.status === "in-progress" ? form.memberFormId : null,
      link: journeyFormLink(patientId, form),
    })),
  };
};

export const journeyOverview = (
  roadmap: CareJourneyRoadmap | null,
  patientId: string
): JourneyOverview => {
  const suggestedId = roadmap?.currentStepId ?? null;
  // Placeholder steps whose forms haven't shipped can't be worked.
  const steps = (roadmap?.steps ?? [])
    .filter((step) => step.status !== "unavailable" && step.forms.length > 0)
    .map((step) => toJourneyStep(step, patientId, suggestedId));
  const suggested = steps.find((step) => step.isSuggested) ?? null;
  return { suggested, steps };
};

/** A patient's care note, as much of it as the Care Management focus reads. */
export interface CareNoteRecord {
  id: string;
  formId: string;
  status: string;
  submittedOn?: string | Date | null;
  updatedOn?: string | Date | null;
  createdOn?: string | Date | null;
}

export const CARE_MANAGEMENT_STEP_ID = "care-management-note";

const timeOf = (at?: string | Date | null) => (at ? new Date(at).getTime() : 0);
const latest = (notes: CareNoteRecord[], at: (note: CareNoteRecord) => number) =>
  [...notes].sort((a, b) => at(b) - at(a))[0];

/**
 * A Care Management note as a focus the clinician can choose, like a journey
 * step with one form. It's in progress while there's a draft (Resume),
 * completed once one has been submitted since the visit started, and
 * otherwise ready to start. "Last done" is the latest one ever submitted.
 */
export const careManagementStep = (
  patientId: string,
  notes: CareNoteRecord[],
  visitStartedAt: string | null
): JourneyStep => {
  const careManagement = notes.filter((note) => note.formId === CARE_MANAGEMENT_FORM_ID);
  const submitted = careManagement.filter((note) => note.status === "submitted");
  const draft = latest(
    careManagement.filter((note) => note.status === "draft"),
    (note) => timeOf(note.updatedOn ?? note.createdOn)
  );
  const lastSubmitted = latest(submitted, (note) => timeOf(note.submittedOn));
  const submittedThisVisit =
    lastSubmitted && visitStartedAt && timeOf(lastSubmitted.submittedOn) >= timeOf(visitStartedAt)
      ? lastSubmitted
      : undefined;

  const status: CareJourneyStatus = draft
    ? "in-progress"
    : submittedThisVisit
    ? "completed"
    : "not-started";
  const submittedAt = submittedThisVisit?.submittedOn
    ? new Date(submittedThisVisit.submittedOn).toISOString()
    : null;

  return {
    id: CARE_MANAGEMENT_STEP_ID,
    name: "Care Management",
    status,
    isSuggested: false,
    isOptional: false,
    isCompleted: status === "completed",
    doneCount: status === "completed" ? 1 : 0,
    lastDoneAt: lastSubmitted?.submittedOn
      ? new Date(lastSubmitted.submittedOn).toISOString()
      : null,
    forms: [
      {
        clinicalFormId: CARE_MANAGEMENT_FORM_ID,
        name: "Care Management",
        isCareNote: true,
        status,
        submittedAt,
        draftId: draft?.id ?? null,
        link: draft
          ? { label: "Resume", to: `/patients/${patientId}/care-note/${draft.id}` }
          : {
              label: status === "completed" ? "Start again" : "Start",
              to: newCareNotePath(patientId, CARE_MANAGEMENT_FORM_ID),
            },
      },
    ],
  };
};

/** A form submitted during this visit, from the appointment's activity log. */
export interface VisitCompletion {
  /** The member form that was submitted. */
  memberFormId: string;
  completedAt: string | null;
}

/** Where to start a form again once it's done. */
const startAgainLink = (patientId: string, form: JourneyForm): JourneyFormLink => ({
  label: "Start again",
  to: form.isCareNote
    ? newCareNotePath(patientId, form.clinicalFormId)
    : `/patients/${patientId}/assessments/${form.clinicalFormId}/start`,
});

const withCompletions = (
  step: JourneyStep,
  completions: Map<string, VisitCompletion[]>,
  patientId: string
): JourneyStep => {
  let changed = false;
  const forms = step.forms.map((form) => {
    const done = completions.get(form.clinicalFormId);
    const openDraft =
      form.draftId && !done?.some((c) => c.memberFormId === form.draftId);
    if (!done?.length || form.status === "completed" || openDraft) {
      return form;
    }
    changed = true;
    const latest = done
      .map((c) => c.completedAt)
      .filter((at): at is string => !!at)
      .sort()
      .pop();
    return {
      ...form,
      status: "completed" as CareJourneyStatus,
      submittedAt: latest ?? form.submittedAt,
      draftId: null,
      link: startAgainLink(patientId, form),
    };
  });
  if (!changed) return step;

  const doneCount = forms.filter((form) => form.status === "completed").length;
  const isCompleted = step.isCompleted || doneCount === forms.length;
  const lastDoneAt =
    forms
      .map((form) => form.submittedAt)
      .filter((at): at is string => !!at)
      .sort()
      .pop() ?? step.lastDoneAt;
  return {
    ...step,
    forms,
    doneCount,
    lastDoneAt,
    isCompleted,
    status: isCompleted ? "completed" : "in-progress",
  };
};

export const withVisitCompletions = (
  overview: JourneyOverview,
  completions: Map<string, VisitCompletion[]>,
  patientId: string
): JourneyOverview => {
  if (completions.size === 0) return overview;
  const apply = (step: JourneyStep) => withCompletions(step, completions, patientId);
  const steps = overview.steps.map(apply);
  return {
    ...overview,
    suggested: steps.find((step) => step.isSuggested) ?? null,
    steps,
    ...(overview.extras ? { extras: overview.extras.map(apply) } : {}),
  };
};
